import React from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import SEO from "../components/SEO";
import weighingBayImg from "../assets/images/authentic/warehouse_weighing_bay.webp";
import officePartnersImg from "../assets/images/authentic/office_partners.webp";
import {
  Handshake,
  Sliders,
  Scale,
  ArrowRight,
  Phone,
  MessageCircle,
  FileCheck,
  Eye,
} from "lucide-react";

const WoolAgentBikaner = () => {
  const { lPath, isRTL } = useLanguage();

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://skwoolens.trivanahospitality.com/en"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Wool Agent in Bikaner",
        "item": "https://skwoolens.trivanahospitality.com/en/wool-agent-bikaner"
      }
    ]
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Wool Mandi Commission Agent & Sourcing Services",
    "provider": {
      "@type": "WholesaleStore",
      "name": "S.K. Woollens",
      "telephone": "+91-7014581250",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "B-26, Un Mandi, Antyodaya Nagar",
        "addressLocality": "Bikaner",
        "addressRegion": "Rajasthan",
        "postalCode": "334001",
        "addressCountry": "IN"
      }
    },
    "description": "On-the-ground wool commission agent and procurement brokerage at Bikaner Un Mandi. Lot sampling, quality inspection, auction representation, certified weighing, and dispatch for spinning mills across India.",
    "areaServed": "India"
  };

  const schema = [breadcrumbSchema, serviceSchema];

  return (
    <div className="space-y-16 sm:space-y-24 py-6">
      <SEO
        title="Wool Agent in Bikaner | Commission Mandi Brokerage | S.K. Woollens"
        description="Looking for an experienced wool agent in Bikaner? S.K. Woollens provides on-the-ground Un Mandi representation, lot inspection, certified weighing & procurement for mills."
        keywords="wool agent in Bikaner, wool commission agent Bikaner, raw wool trading Bikaner, Bikaner wool mandi broker, wool procurement agent Rajasthan"
        canonicalUrl="https://skwoolens.trivanahospitality.com/en/wool-agent-bikaner"
        schema={schema}
      />

      {/* 1. Header Banner */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-gradient-to-br from-wool-sand/50 via-white to-wool-sand/30 border border-wool-border rounded-3xl p-8 sm:p-14 shadow-subtle">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7 space-y-4">
              <div className="wool-badge inline-flex">
                <Handshake className="w-3.5 h-3.5 text-wool-bronze" />
                <span>On-The-Ground Mandi Representation</span>
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-wool-charcoal tracking-tight font-display">
                Wool Agent & Commission Broker in Bikaner
              </h1>
              <p className="text-base sm:text-lg text-wool-slate leading-relaxed">
                Operating directly inside Bikaner's Un Mandi, S.K. Woollens serves as a trusted commission agent and procurement partner for spinning mills, yarn manufacturers, and outstation wool merchants who require expert local representation in Asia's largest carpet wool trading mandi.
              </p>

              <div className="p-4 bg-white/80 border border-wool-border rounded-xl text-xs sm:text-sm text-wool-slate">
                <span className="font-semibold text-wool-bronze block mb-1">
                  Your Eyes & Hands at the Bikaner Mandi:
                </span>
                We inspect newly arrived wool clips, verify clean yield % and staple length, negotiate fair market rates, and manage certified weighing and loading directly to your mill gate.
              </div>

              <div className="pt-2 flex flex-wrap items-center gap-3">
                <a
                  href="https://wa.me/917014581250?text=Hi%2C%20I%20am%20looking%20for%20a%20trusted%20wool%20agent%20in%20Bikaner%20mandi"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary inline-flex items-center gap-2"
                >
                  <MessageCircle className="w-4 h-4 text-emerald-300" />
                  <span>Enquire for Mandi Agency</span>
                  <ArrowRight className={`w-4 h-4 ${isRTL ? "rotate-180" : ""}`} />
                </a>

                <a href="tel:+919829109294" className="btn-secondary flex items-center gap-2">
                  <Phone className="w-4 h-4 text-wool-bronze" />
                  <span>Call Sunil Banthia: +91 9829109294</span>
                </a>
              </div>
            </div>

            <div className="lg:col-span-5 relative">
              <div className="relative rounded-2xl overflow-hidden shadow-xl border border-wool-border bg-white">
                <img
                  src={officePartnersImg}
                  alt="S.K. Woollens Partners Nikhil Banthia and Sunil Banthia in Bikaner Office"
                  className="w-full h-72 sm:h-80 object-cover"
                  loading="eager"
                />
                <div className="p-3 bg-wool-sand/80 border-t border-wool-border text-center text-xs font-semibold text-wool-charcoal">
                  Direct Partner Access: Sunil Banthia & Nikhil Banthia
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. What an Experienced Mandi Wool Agent Does */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white border border-wool-border rounded-3xl p-6 sm:p-10 shadow-subtle space-y-6">
          <div className="max-w-3xl space-y-3">
            <div className="wool-badge">
              <Eye className="w-3.5 h-3.5 text-wool-bronze" />
              <span>Full-Cycle Representation</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-wool-charcoal font-display">
              Why Outstation Spinning Mills Rely on a Local Bikaner Wool Agent
            </h2>
            <p className="text-sm sm:text-base text-wool-slate leading-relaxed">
              Raw wool trading in Bikaner requires daily vigilance. Thousands of kilograms of wool arrive daily from regional pastoralist belts, each lot carrying subtle differences in moisture, sand content, burr presence, and fibre fineness. Outstation spinning mills cannot afford to buy uninspected lots remotely. As your registered agent in Bikaner:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-4">
            <div className="wool-card p-6 space-y-3 bg-wool-sand/20">
              <Eye className="w-6 h-6 text-wool-bronze" />
              <h3 className="text-base font-bold text-wool-charcoal">Hands-On Lot Sampling & Grading</h3>
              <p className="text-xs text-wool-slate leading-relaxed">
                We physically open bales, draw core samples, evaluate fibre crimp and handle, and assess clean yield percentages before purchase commitments are made.
              </p>
            </div>

            <div className="wool-card p-6 space-y-3 bg-wool-sand/20">
              <Scale className="w-6 h-6 text-wool-bronze" />
              <h3 className="text-base font-bold text-wool-charcoal">Certified Weighing & Tare Supervision</h3>
              <p className="text-xs text-wool-slate leading-relaxed">
                We supervise weighing at certified weighbridges, verify tare weights, and ensure you pay strictly for genuine net wool weight with zero discrepancy.
              </p>
            </div>

            <div className="wool-card p-6 space-y-3 bg-wool-sand/20">
              <FileCheck className="w-6 h-6 text-wool-bronze" />
              <h3 className="text-base font-bold text-wool-charcoal">Transparent Market Intelligence</h3>
              <p className="text-xs text-wool-slate leading-relaxed">
                We provide daily mandi rate updates, arrivals volume analysis, and seasonal price forecasts so your mill procurement team can time bulk purchases strategically.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 3. Agency Process Workflow */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto space-y-3 mb-10">
          <div className="wool-badge">
            <Sliders className="w-3.5 h-3.5 text-wool-bronze" />
            <span>Structured Process</span>
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-wool-charcoal font-display">
            How Our Bikaner Mandi Commission Service Works
          </h2>
          <p className="text-sm sm:text-base text-wool-muted">
            A transparent, 4-step workflow ensuring full accountability from auction floor to mill delivery.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          <div className="bg-white border border-wool-border rounded-2xl p-5 shadow-sm space-y-2">
            <span className="text-[11px] font-bold text-wool-bronze uppercase">Step 01</span>
            <h4 className="text-base font-bold text-wool-charcoal">Requirement Brief</h4>
            <p className="text-xs text-wool-muted">
              You provide target micron (e.g. 30+ or 32–34+), staple length, color preference, and budget parameters.
            </p>
          </div>

          <div className="bg-white border border-wool-border rounded-2xl p-5 shadow-sm space-y-2">
            <span className="text-[11px] font-bold text-wool-bronze uppercase">Step 02</span>
            <h4 className="text-base font-bold text-wool-charcoal">Lot Selection & Inspection</h4>
            <p className="text-xs text-wool-muted">
              We locate matching lots on the mandi floor, perform tactile inspection, and share photographs and sample reports.
            </p>
          </div>

          <div className="bg-white border border-wool-border rounded-2xl p-5 shadow-sm space-y-2">
            <span className="text-[11px] font-bold text-wool-bronze uppercase">Step 03</span>
            <h4 className="text-base font-bold text-wool-charcoal">Negotiation & Weighing</h4>
            <p className="text-xs text-wool-muted">
              We negotiate the best rate on your behalf, supervise weighbridge tallying, and verify packaging integrity.
            </p>
          </div>

          <div className="bg-white border border-wool-border rounded-2xl p-5 shadow-sm space-y-2">
            <span className="text-[11px] font-bold text-wool-bronze uppercase">Step 04</span>
            <h4 className="text-base font-bold text-wool-charcoal">Baling & Dispatch</h4>
            <p className="text-xs text-wool-muted">
              Bales are pressed, tagged, loaded onto dedicated freight trucks, and tracked until arrival at your spinning facility.
            </p>
          </div>
        </div>
      </section>

      {/* 4. Weighing Bay & Quality Control Showcase */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center bg-wool-charcoal text-white rounded-3xl p-6 sm:p-12 shadow-2xl">
          <div className="lg:col-span-6 space-y-4">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-white/10 text-wool-sand text-xs font-bold uppercase tracking-wider rounded-full">
              <Scale className="w-3.5 h-3.5 text-wool-sand" />
              <span>Weighing & Verification Infrastructure</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight font-display">
              Dedicated Weighing Bay & Lot Traceability
            </h2>
            <p className="text-xs sm:text-sm text-wool-sand/80 leading-relaxed">
              We maintain direct control over weighbridge verification at our facility. Every consignment is weighed with certified scales and recorded with detailed slip copies, ensuring full transparency in financial settlements.
            </p>
            <div className="p-4 bg-white/5 border border-white/10 rounded-xl text-xs text-wool-sand/80 space-y-1">
              <div className="font-semibold text-white">Direct Mandi Office:</div>
              <div>B-26, Un Mandi, Antyodaya Nagar, Bikaner, Rajasthan 334001</div>
              <div className="text-emerald-400 font-semibold pt-1">Active daily during mandi auction hours</div>
            </div>
          </div>

          <div className="lg:col-span-6 relative rounded-2xl overflow-hidden border border-white/20">
            <img
              src={weighingBayImg}
              alt="S.K. Woollens Weighing Bay and Mandi Godown in Bikaner"
              className="w-full h-72 sm:h-80 object-cover"
              loading="lazy"
            />
            <div className="p-3 bg-black/60 backdrop-blur-md border-t border-white/20 text-center text-xs text-wool-sand">
              Commercial Weighing Bay — S.K. Woollens Bikaner Godown
            </div>
          </div>
        </div>
      </section>

      {/* 5. Related Pages Navigation */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="border border-wool-border bg-white rounded-3xl p-6 sm:p-10 shadow-subtle space-y-6">
          <h3 className="text-xl font-bold text-wool-charcoal font-display">
            Explore Related Wool Capabilities
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Link
              to={lPath("/wool-supplier-bikaner")}
              className="p-4 border border-wool-border rounded-xl hover:border-wool-bronze hover:bg-wool-sand/20 transition flex flex-col justify-between"
            >
              <div>
                <h4 className="text-sm font-bold text-wool-charcoal">Wool Supplier in Bikaner</h4>
                <p className="text-xs text-wool-muted mt-1">Direct mandi presence and 60-year supply legacy.</p>
              </div>
              <span className="text-xs font-semibold text-wool-bronze mt-3 inline-flex items-center gap-1">
                Read more <ArrowRight className="w-3 h-3" />
              </span>
            </Link>

            <Link
              to={lPath("/carpet-wool-supplier-india")}
              className="p-4 border border-wool-border rounded-xl hover:border-wool-bronze hover:bg-wool-sand/20 transition flex flex-col justify-between"
            >
              <div>
                <h4 className="text-sm font-bold text-wool-charcoal">Carpet Wool Supplier India</h4>
                <p className="text-xs text-wool-muted mt-1">Specialized 30+ & 32-34+ micron carpet grades for yarn spinning.</p>
              </div>
              <span className="text-xs font-semibold text-wool-bronze mt-3 inline-flex items-center gap-1">
                Read more <ArrowRight className="w-3 h-3" />
              </span>
            </Link>

            <Link
              to={lPath("/bulk-wool-supply")}
              className="p-4 border border-wool-border rounded-xl hover:border-wool-bronze hover:bg-wool-sand/20 transition flex flex-col justify-between"
            >
              <div>
                <h4 className="text-sm font-bold text-wool-charcoal">Bulk Wool Supply</h4>
                <p className="text-xs text-wool-muted mt-1">Multi-tonne mill supply contracts and buffer storage.</p>
              </div>
              <span className="text-xs font-semibold text-wool-bronze mt-3 inline-flex items-center gap-1">
                Read more <ArrowRight className="w-3 h-3" />
              </span>
            </Link>

            <Link
              to={lPath("/partners")}
              className="p-4 border border-wool-border rounded-xl hover:border-wool-bronze hover:bg-wool-sand/20 transition flex flex-col justify-between"
            >
              <div>
                <h4 className="text-sm font-bold text-wool-charcoal">Leadership & Partners</h4>
                <p className="text-xs text-wool-muted mt-1">Meet Sunil Banthia and Nikhil Banthia.</p>
              </div>
              <span className="text-xs font-semibold text-wool-bronze mt-3 inline-flex items-center gap-1">
                Read more <ArrowRight className="w-3 h-3" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* 6. Direct Contact CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-gradient-to-r from-wool-sand via-wool-sand-dark to-wool-sand border border-wool-border rounded-3xl p-8 sm:p-12 text-center space-y-6 shadow-premium">
          <div className="max-w-2xl mx-auto space-y-2">
            <h3 className="text-2xl sm:text-3xl font-extrabold text-wool-charcoal font-display">
              Appoint S.K. Woollens as Your Bikaner Mandi Agent
            </h3>
            <p className="text-xs sm:text-sm text-wool-slate">
              Connect directly with Sunil Banthia to discuss on-the-ground procurement, commission rates, and upcoming wool lot auctions in Bikaner.
            </p>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-4">
            <a
              href="https://wa.me/917014581250?text=Hi%2C%20I%20would%20like%20to%20discuss%20wool%20agency%20and%20procurement%20services%20in%20Bikaner%20mandi"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary inline-flex items-center gap-2"
            >
              <MessageCircle className="w-4 h-4 text-emerald-300" />
              <span>WhatsApp Agency Desk</span>
            </a>

            <a
              href="tel:+919829109294"
              className="btn-secondary flex items-center gap-2 bg-white"
            >
              <Phone className="w-4 h-4 text-wool-bronze" />
              <span>Call Sunil Banthia: +91 9829109294</span>
            </a>

            <Link to={lPath("/contact")} className="btn-secondary bg-white">
              <span>View Contact Desk</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WoolAgentBikaner;
